import React, { useState } from 'react';

interface OnboardingData {
  companyInfo: {
    carrierName: string;
    mcNumber: string;
    dotNumber: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
  };
  contactInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    title: string;
  };
  insurance: {
    coiFileName: string;
    autoLiability: string;
    cargoInsurance: string;
    generalLiability: string;
    expirationDate: string;
  };
  taxPayment: {
    w9FileName: string;
    paymentMethod: string;
    bankName: string;
    routingNumber: string;
    accountNumber: string;
    factoringCompany: string;
    noaFileName: string;
  };
  agreement: {
    agreedToTerms: boolean;
    signature: string;
    signedDate: string;
  };
}

interface Step6Props {
  data: OnboardingData;
  brokerName: string;
  onBack: () => void;
  onSubmit: () => void;
}

const Step6Review: React.FC<Step6Props> = ({ data, brokerName, onBack, onSubmit }) => {
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!confirmed) {
      setError('Please confirm that all information is accurate');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await onSubmit();
    } catch (err) {
      setError('Failed to submit. Please try again.');
      setSubmitting(false);
    }
  };

  const formatCurrency = (value: string) => {
    if (!value) return 'N/A';
    return `$${value}`;
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const maskAccountNumber = (num: string) => {
    if (!num || num.length < 4) return num;
    return '••••' + num.slice(-4);
  };

  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case 'ach': return 'ACH / Direct Deposit';
      case 'factoring': return 'Factoring Company';
      case 'check': return 'Paper Check';
      default: return method;
    }
  };

  const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900">{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );

  const Field = ({ label, value }: { label: string; value: string | React.ReactNode }) => (
    <div className="py-2">
      <dt className="text-sm text-gray-500">{label}</dt>
      <dd className="text-sm font-medium text-gray-900 mt-1">{value || 'N/A'}</dd>
    </div>
  );

  return (
    <div>
      <h2 className="text-xl font-semibold mb-2">Review Your Information</h2>
      <p className="text-gray-600 mb-6">
        Please review all information before submitting. Once submitted, your application will be 
        reviewed by {brokerName || 'the broker'}.
      </p>

      <div className="space-y-6">
        {/* Company Information */}
        <Section title="Company Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            <Field label="Carrier Name" value={data.companyInfo.carrierName} />
            <Field label="MC Number" value={`MC-${data.companyInfo.mcNumber}`} />
            <Field label="DOT Number" value={`DOT-${data.companyInfo.dotNumber}`} />
            <Field 
              label="Address" 
              value={`${data.companyInfo.address}, ${data.companyInfo.city}, ${data.companyInfo.state} ${data.companyInfo.zipCode}`} 
            />
          </div>
        </Section>

        {/* Contact Information */}
        <Section title="Contact Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            <Field label="Name" value={`${data.contactInfo.firstName} ${data.contactInfo.lastName}`} />
            <Field label="Title" value={data.contactInfo.title} />
            <Field label="Email" value={data.contactInfo.email} />
            <Field label="Phone" value={data.contactInfo.phone} />
          </div>
        </Section>

        {/* Insurance Information */}
        <Section title="Insurance Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            <Field 
              label="Certificate of Insurance" 
              value={
                <span className="flex items-center text-green-600">
                  <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {data.insurance.coiFileName}
                </span>
              } 
            />
            <Field label="Expiration Date" value={formatDate(data.insurance.expirationDate)} />
            <Field label="Auto Liability" value={formatCurrency(data.insurance.autoLiability)} />
            <Field label="Cargo Insurance" value={formatCurrency(data.insurance.cargoInsurance)} />
            {data.insurance.generalLiability && (
              <Field label="General Liability" value={formatCurrency(data.insurance.generalLiability)} />
            )}
          </div>
        </Section>

        {/* Tax & Payment Information */}
        <Section title="Tax & Payment Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            <Field 
              label="W-9 Form" 
              value={
                <span className="flex items-center text-green-600">
                  <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {data.taxPayment.w9FileName}
                </span>
              } 
            />
            <Field label="Payment Method" value={getPaymentMethodLabel(data.taxPayment.paymentMethod)} />
            
            {data.taxPayment.paymentMethod === 'ach' && (
              <>
                <Field label="Bank Name" value={data.taxPayment.bankName} />
                <Field label="Account Number" value={maskAccountNumber(data.taxPayment.accountNumber)} />
              </>
            )}
            
            {data.taxPayment.paymentMethod === 'factoring' && (
              <>
                <Field label="Factoring Company" value={data.taxPayment.factoringCompany} />
                <Field 
                  label="Notice of Assignment" 
                  value={
                    <span className="flex items-center text-green-600">
                      <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {data.taxPayment.noaFileName}
                    </span>
                  } 
                />
              </>
            )}
          </div>
        </Section>

        {/* Agreement */}
        <Section title="Agreement">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            <Field 
              label="Agreement Status" 
              value={
                <span className="flex items-center text-green-600">
                  <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Agreed & Signed
                </span>
              } 
            />
            <Field label="Signed Date" value={formatDate(data.agreement.signedDate)} />
            <Field 
              label="Signature" 
              value={
                <span className="text-lg italic" style={{ fontFamily: 'cursive, serif' }}>
                  {data.agreement.signature}
                </span>
              } 
            />
          </div>
        </Section>

        {/* Confirmation Checkbox */}
        <div className={`p-4 rounded-lg ${error && !confirmed ? 'bg-red-50 border border-red-300' : 'bg-yellow-50 border border-yellow-200'}`}>
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => {
                setConfirmed(e.target.checked);
                if (e.target.checked) setError('');
              }}
              className="h-5 w-5 text-blue-600 focus:ring-blue-500 mt-0.5"
            />
            <span className="ml-3 text-gray-700">
              I confirm that all information provided is accurate and complete. I understand that 
              providing false information may result in termination of the carrier agreement.
            </span>
          </label>
          {error && (
            <p className="text-red-500 text-sm mt-2 ml-8">{error}</p>
          )}
        </div>

        {/* What Happens Next */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <h4 className="font-semibold text-blue-800 mb-2">What Happens Next?</h4>
          <ol className="text-sm text-blue-800 list-decimal list-inside space-y-1">
            <li>Your application will be reviewed by {brokerName || 'the broker'}</li>
            <li>Your MC/DOT numbers and insurance will be verified</li>
            <li>You'll receive an email once your application is approved</li>
            <li>After approval, you'll be able to accept loads</li>
          </ol>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-8">
        <button
          onClick={onBack}
          disabled={submitting}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        >
          ← Back
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="px-8 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 flex items-center"
        >
          {submitting ? (
            <>
              <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Submitting...
            </>
          ) : (
            'Submit Application'
          )}
        </button>
      </div>
    </div>
  );
};

export default Step6Review;